import React, { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import Input from "components/Input";
import Btn from "components/Btn";
import AddMovieHeader from "../components/AddMovieHeader";
import { useNavigate, useSearchParams } from "react-router-dom";
import useMutation from "hooks/useMutation";
import { toast } from "react-toastify";
import { uploadFile } from "../components/uploadFile";

type Mode = "view" | "edit";

type BasicInfo = {
  number: number | string;
  title: string;
  description: string;
  key?: string;
  file?: File | null;
};

type Props = {
  defaultMode?: Mode;
  initialValues?: Partial<BasicInfo>;
  onSave?: (values: BasicInfo) => void;
  onCancel?: () => void;
  details?: any;
};

export default function AddEpisode({
  defaultMode = "view",
  initialValues,
  details,
  onSave,
  onCancel,
}: Props) {
  console.log(details);

  const [mode, setMode] = useState<Mode>(defaultMode);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mediaId = searchParams.get("id") || "";
  const seasonId = searchParams.get("season_id") || "";
  console.log({ mediaId, seasonId });

  const { loading: saving, mutate } = useMutation({
    method: "post",
    url: `/entertainment/movie-box/medias/${mediaId}/seasons/${seasonId}/episodes`,
  });

  const methods = useForm<BasicInfo>({
    defaultValues: {
      number: (initialValues as any)?.number ?? 1,
      title: initialValues?.title ?? "",
      description: initialValues?.description ?? "",
      key: (initialValues as any)?.key ?? "",
      file: null,
    },
    mode: "onChange",
  });

  const { handleSubmit, setValue, watch } = methods;
  const f = watch("file");
  const fileName = useMemo(() => {
    if (!f) return "";
    if (typeof f === "string") return f;
    if (f instanceof File) return f.name;
    return "";
  }, [f]);

  const disabled = useMemo(() => mode === "view", [mode]);

  const onSubmit = async (values: BasicInfo & { file: Blob | File }) => {
    const uploadUrl = await uploadFile({
      name: fileName,
      file: values.file,
      mediaId,
      type: "episode",
    });
    const payload = {
      title: values.title,
      number: Number(values.number),
      description: values.description,
      key: values.key || "",
      path:uploadUrl?.response.data
    };

    await mutate({
      payload,
      onSuccess: () => {
        toast.success("Episode created");
        setMode("view");
        navigate(`/movie-box/add-movie/episode?id=${mediaId}`);
      },
      onError: (err) => {
        toast.error("Failed to create episode");
        console.error(err);
      },
    });
  };

  const onEditToggle = () => setMode((m) => (m === "edit" ? "view" : "edit"));
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setValue("file", f);
  };

  return (
    <FormProvider {...methods}>
      <AddMovieHeader
        showProgress={true}
        currentStep={1}
        onContinue={handleSubmit(onSubmit)}
        loading={saving}
      />
      <div className="max-w-4xl mx-auto mt-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-[#0a2342]">
            Basic -information
          </h1>
          <div className="flex gap-3">
            <Btn
              content={mode === "edit" ? "Cancel" : "Edit"}
              variant={mode === "edit" ? "outline" : "contained"}
              click={mode === "edit" ? () => setMode("view") : onEditToggle}
              className="px-5"
              disabled={false}
              sx={{}}
              loading={false}
              color={"#0a66c2"}
            />
            {mode === "edit" && (
              <Btn
                content={"Save changes"}
                click={() => handleSubmit(onSubmit)()}
                className="px-5"
                variant="contained"
                disabled={false}
                sx={{}}
                loading={false}
                color={"#0a66c2"}
              />
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Episode number */}

          <Input
            name="number"
            placeholder="Episode number"
            disabled
            label="Episode number"
            className="my-2"
          />

          {/* Episode title */}

          <Input
            name="title"
            placeholder="Enter episode title"
            disabled={disabled}
            label="Episode title"
            className="my-2"
          />

          {/* Episode description */}
          <Input
            name="description"
            type="textarea"
            placeholder="Describe the episode…"
            rows={6}
            disabled={disabled}
            label="Episode description"
            className="my-2"
          />

          {/* File key */}
          <Input
            name="key"
            placeholder="Enter file key (optional)"
            disabled={disabled}
            label="File key"
            className="my-2"
          />

          {/* Movie file */}
          <div className="mt-8">
            <p className="text-lg font-semibold mb-3">Movie file</p>
            <div
              className={`border-2 border-dashed rounded-xl p-6 ${
                disabled ? "bg-gray-50" : "bg-white"
              } text-center`}
            >
              <input
                id="movie-file"
                type="file"
                className="hidden"
                onChange={handleFileSelect}
                disabled={disabled}
                accept=".pdf,image/jpeg,image/png,video/*"
              />
              <label
                htmlFor="movie-file"
                className={`cursor-pointer inline-flex items-center gap-2 text-primary ${
                  disabled ? "pointer-events-none opacity-60" : ""
                }`}
              >
                <span className="inline-block w-6 h-6 rounded-full border border-primary flex items-center justify-center">
                  +
                </span>
                <span>Upload</span>
              </label>
              <p className="text-xs text-gray-500 mt-2">
                PDF, JPEG, or PNG less than 3MB
              </p>

              {fileName && (
                <p className="mt-2 text-sm text-green-600">✓ {fileName}</p>
              )}
            </div>
          </div>
        </form>
      </div>
    </FormProvider>
  );
}
